import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import VoiceInputButton from '../../../components/ui/VoiceInputButton';
import { sanitizeInput } from '../../../utils/sanitizer';

const SymptomCategorySelector = ({ selectedCategories, onToggle, otherText, onOtherTextChange }) => {
  const symptomCategories = [
    { id: 'pain', label: 'Pain/Discomfort', icon: 'Zap' },
    { id: 'tiredness', label: 'Extreme Tiredness', icon: 'Battery' },
    { id: 'dizziness', label: 'Dizziness/Confusion', icon: 'CircleDashed' },
    { id: 'breathing', label: 'Breathing Changes', icon: '/assets/images/MyBodyToday_logo_design2-removebg-preview1-1770673381636.png' },
    { id: 'stomach', label: 'Stomach/Digestion Issues', icon: 'Apple' },
    { id: 'head', label: 'Head/Vision Issues', icon: 'Eye' },
    { id: 'muscle', label: 'Muscle/Joint Problems', icon: 'Dumbbell' },
    { id: 'fever', label: 'Fever/Temperature', icon: 'Thermometer' },
    { id: 'mood', label: 'Mood/Behavior Changes', icon: 'Brain' },
    { id: 'unexplainable', label: 'Something Unexplainable', icon: 'HelpCircle' },
    { id: 'others', label: 'Others', icon: 'MoreHorizontal' }
  ];

  const handleOtherTextChange = (e) => {
    const rawValue = e?.target?.value;
    const sanitized = sanitizeInput(rawValue, {
      maxLength: 500,
      allowHtml: false,
      removeControlChars: false
    });
    onOtherTextChange?.(sanitized);
  };

  const handleVoiceChange = (text) => {
    onOtherTextChange?.(text);
  };

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h3 className="text-lg md:text-xl lg:text-2xl font-heading font-bold text-foreground mb-2">
          What symptoms are you experiencing?
        </h3>
        <p className="text-sm md:text-base text-muted-foreground">
          Select all that apply
        </p>
      </div>
      
      {/* Helpful tip */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <div className="flex items-start gap-2">
          <Icon name="Info" size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-700">
            <strong>Tip:</strong> You can select multiple symptoms. Choose all categories that describe what you're experiencing today.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
        {symptomCategories?.map((category) => {
          const isSelected = selectedCategories?.includes(category?.id);
          return (
            <button
              key={category?.id}
              onClick={() => onToggle(category?.id)}
              className={`
                flex flex-col items-center justify-center p-4 md:p-5 rounded-xl
                border-2 transition-smooth min-h-[120px] md:min-h-[140px]
                ${isSelected 
                  ? 'border-primary bg-primary/5 shadow-therapeutic-md' 
                  : 'border-border bg-card hover:border-primary/30 hover:shadow-therapeutic'
                }
                focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background
                active:scale-[0.97]
              `}
              aria-pressed={isSelected}
              aria-label={category?.label}
            >
              <div className={`
                flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-lg mb-3
                ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
                transition-smooth
              `}>
                <Icon name={category?.icon} size={24} strokeWidth={2} />
              </div>
              <span className={`
                text-xs md:text-sm font-medium text-center
                ${isSelected ? 'text-foreground' : 'text-muted-foreground'}
              `}>
                {category?.label}
              </span>
              {isSelected && (
                <Icon 
                  name="CheckCircle2" 
                  size={16} 
                  className="text-primary mt-2"
                  strokeWidth={2.5}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Others Free Text Input */}
      {selectedCategories?.includes('others') && (
        <div className="mt-6 bg-card rounded-xl p-4 md:p-6 border border-border space-y-3">
          <div className="flex items-center justify-between">
            <label htmlFor="others-input" className="block text-sm md:text-base font-medium text-foreground">
              Describe your symptom
            </label>
            <VoiceInputButton
              value={otherText || ''}
              onChange={handleVoiceChange}
              maxLength={500}
            />
          </div>
          
          <textarea
            id="others-input"
            value={otherText || ''}
            onChange={handleOtherTextChange}
            placeholder="Please describe the symptom you're experiencing..."
            rows="3"
            maxLength={500}
            className="
              w-full px-4 py-3 rounded-lg border border-border bg-background
              text-foreground placeholder:text-muted-foreground
              focus:outline-none focus:ring-3 focus:ring-ring focus:border-ring
              transition-smooth resize-none text-sm md:text-base
            "
            aria-label="Other symptom description"
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Be as specific as possible to help track your symptoms
            </p>
            <p className="text-xs text-muted-foreground">
              {(otherText?.length || 0)}/500
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SymptomCategorySelector;